import { LatLngExpression } from 'leaflet'
import { memo } from 'react'
import { Circle, Tooltip } from 'react-leaflet'
import { getPositionName } from 'src/utils/function'
import { IPosition } from '../../../../../generated-types'
import { useShowHiddenPositions } from '../ShowHiddenPositionsContext'
import {
  cornerHitAreaRadiusMultiplier,
  deviationPositions,
  grayedOpacity,
  mapOptions,
  opacity,
  totalDeviationMultiplier,
} from '../constraints'
import { useMooringsCtx } from 'src/hooks/context-hooks'

interface ICorner {
  position: IPosition
  navigateToPosition: (positionId: number) => void
  size?: number
  showCriticalPositions?: boolean
}

export const Corner: React.FC<ICorner> = memo(
  ({ position, navigateToPosition, size, showCriticalPositions = false }) => {
    const showHiddenPositions = useShowHiddenPositions()
    size = size || 4
    const generateCorners = true
    const { isCritical } = useMooringsCtx()
    const shouldHighlightCritical = !!(isCritical || showCriticalPositions)
    // Hidden positions are drawn greyed out when "show hidden" is on, so they can be found.
    if (position.isHidden && !showHiddenPositions) return <></>
    if (!generateCorners) return <></>
    if (position.isHidden) position = { ...position, isTransparent: true }

    if (position.deviationsCount != 0) {
      deviationPositions.push([
        position.latitude,
        position.longitude,
        totalDeviationMultiplier * position.deviationsCount,
      ])
    }

    const latlngs = [position.latitude, position.longitude]
    const pathOptions = {
      radius: size,
      fillColor:
        position.deviationsCount > 0
          ? opacity !== 1.0
            ? mapOptions.cornerColor
            : mapOptions.deviationColor
          : mapOptions.cornerColor,
      fillOpacity: position.isTransparent ? grayedOpacity : opacity,
      stroke: !!(position.isCritical && shouldHighlightCritical),
      color:
        position.isCritical && shouldHighlightCritical
          ? position.deviationsCount > 0
            ? mapOptions.deviationColor
            : mapOptions.cornerColor
          : undefined,
      weight: position.isCritical && shouldHighlightCritical ? 3 : 0,
      pane: position.isWorkAssignmentPosition ? 'cornerUpdated' : 'corner',
    }

    const tooltip = `${mapOptions.lang.corner} ${getPositionName(position)}`

    return (
      <>
        <Circle
          key={position.id}
          center={latlngs as LatLngExpression}
          pathOptions={pathOptions}
          radius={position.isCritical && shouldHighlightCritical ? pathOptions.radius + 2 : pathOptions.radius}
          pane={pathOptions.pane}
          eventHandlers={{
            add: (e: any) => {
              const el = (e.target as L.Circle).getElement()
              if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-position-marker')
            },
          }}
        />
        {/* The larger hit area sits on top of the visible circle, so it carries the tooltip as well as
          the click; on the circle underneath the tooltip would never be triggered. */}
        <Circle
          center={latlngs as LatLngExpression}
          pathOptions={{ fillOpacity: 0, stroke: false, pane: pathOptions.pane }}
          radius={pathOptions.radius * cornerHitAreaRadiusMultiplier}
          pane={pathOptions.pane}
          eventHandlers={{
            click: e => {
              navigateToPosition(position.id)
            },
          }}
        >
          <Tooltip>
            <strong data-test-id="map-hover-tooltip">{tooltip}</strong>
          </Tooltip>
        </Circle>
      </>
    )
  },
)
